// Rendered review (design/screens/02-review.html) with commenting (M4).
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { diffLines } from 'diff';
import { REPO } from '../config';
import type { GitHub } from '../github/client';
import { addHeadingIds, footnoteNumbers, renderBlock, renderMarkdown, type RenderedBlock } from '../markdown/html';
import { loadFileContents, loadPullRequest, type PrDetails, type PrFile } from '../review/api';
import { changeIndexes, diffFile, type DiffItem, type FileDiff } from '../review/blockDiff';
import { outline } from '../review/outline';
import { placeThreads, type PlacedThread } from '../review/threads';
import { markWordChanges } from '../review/wordDiff';
import { anchorElementId, githubBlobUrl, resolveLink } from '../review/links';
import { changedHeadLines, hunksFromPatch, localHunks } from '../review/anchors';
import { ReviewSession } from '../review/session';
import { commentUnits, type BlockUnits } from '../review/units-for-comments';
import { ReplyBox, ReviewBar, ReviewContext, UnitButtons, type ReviewCtx } from './Commenting';
import { ChevronDownIcon, ChevronUpIcon, CloseIcon, CommentIcon, ExternalIcon } from './icons';
import { LinkContext, Mermaid, Rendered, type LinkResolver } from './Rendered';
import { linkProps, navigate, routePath, type Route } from './router';

type Load<T> = { kind: 'loading' } | { kind: 'error'; message: string } | { kind: 'ready'; value: T };
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function ReviewPage({ gh, number, file, anchor }: { gh: GitHub; number: number; file: string | undefined; anchor?: string | undefined }) {
  const [pr, setPr] = useState<Load<PrDetails>>({ kind: 'loading' });
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setPr({ kind: 'loading' });
    loadPullRequest(gh, REPO, number)
      .then((value) => { if (live) setPr({ kind: 'ready', value }); })
      .catch((e: unknown) => { if (live) setPr({ kind: 'error', message: errMsg(e) }); });
    return () => { live = false; };
  }, [gh, number]);
  // After a comment, reply or submit: reload threads and the pending review in place (no spinner).
  const refresh = useCallback(async () => {
    const value = await loadPullRequest(gh, REPO, number);
    setPr({ kind: 'ready', value });
  }, [gh, number]);
  const session = useMemo(() => (pr.kind === 'ready' ? new ReviewSession(gh, pr.value) : null), [gh, pr]);

  if (pr.kind === 'loading') return <main className="hq-page hq-page--wide" aria-busy="true"><div className="hq-skeleton" style={{ width: '60%', height: 28 }} /><div className="hq-skeleton" style={{ width: '40%' }} /></main>;
  if (pr.kind === 'error') return <main className="hq-page"><p className="hq-error" role="alert">Couldn’t load pull request #{number}: {pr.message}</p><a {...linkProps({ name: 'prs' })}>Back to pull requests</a></main>;

  const p = pr.value;
  // A .md file linked from a document but not changed by the PR opens read-only at the PR head.
  const linked: PrFile | undefined = file && !p.files.some((f) => f.path === file)
    ? { path: file, previousPath: null, status: 'unchanged', additions: 0, deletions: 0, patch: null }
    : undefined;
  const current = linked ?? p.files.find((f) => f.path === file) ?? p.files[0];
  return (
    <>
      <section className="hq-prhead">
        <div className="hq-prhead__row">
          <div className="hq-prhead__title">
            <a className="hq-note" {...linkProps({ name: 'prs' })}>← Pull requests</a>
            <h1 className="hq-title hq-title--small">{p.title} <span className="hq-muted">#{p.number}</span></h1>
            <span className="hq-note hq-branches">
              <span>{p.author} wants to merge {p.commits} {p.commits === 1 ? 'commit' : 'commits'} from</span>
              <code>{p.headRef}</code><span>into</span><code>{p.baseRef}</code>
              {p.isDraft && <span className="hq-status hq-status--draft">Draft</span>}
            </span>
          </div>
          <a className="hq-note" href={p.url} target="_blank" rel="noopener noreferrer">Open on GitHub<ExternalIcon /></a>
        </div>
        {p.threadsTruncated && <TruncatedNotice url={p.url} />}
        <nav className="hq-tabs" aria-label="Pull request sections">
          <a href={p.url} target="_blank" rel="noopener noreferrer">Conversation <span className="hq-count">{p.comments}</span><ExternalIcon size={12} /></a>
          <a href={`${p.url}/commits`} target="_blank" rel="noopener noreferrer">Commits <span className="hq-count">{p.commits}</span><ExternalIcon size={12} /></a>
          <span aria-current="page">Files changed <span className="hq-count">{p.files.length}</span></span>
        </nav>
      </section>
      {current ? (
        <FileReview key={current.path} gh={gh} pr={p} file={current} linked={!!linked} anchor={anchor}
          session={session!} refresh={refresh} notify={setNotice} />
      ) : (
        <main className="hq-page"><p className="hq-lead">This pull request doesn’t change any Markdown files.</p></main>
      )}
      {session && <ReviewBar pr={p} session={session} refresh={refresh} notice={notice} />}
    </>
  );
}

type Rendering = { head?: RenderedBlock; base?: RenderedBlock; wordsChanged?: boolean };

function renderItems(diff: FileDiff): Rendering[] {
  addHeadingIds(diff.headTree); // anchors for #links (head only: removed headings don't need targets)
  const hn = footnoteNumbers(diff.headTree), bn = footnoteNumbers(diff.baseTree);
  return diff.items.map((it) => {
    const r: Rendering = {};
    if ('head' in it) r.head = renderBlock(it.head.node, hn);
    if ('base' in it && it.kind !== 'same') r.base = renderBlock(it.base.node, bn);
    if (it.kind === 'modified' && r.head?.kind === 'html' && r.base?.kind === 'html') {
      const w = markWordChanges(r.base.hast, r.head.hast);
      r.head = { kind: 'html', hast: w.hast };
      r.wordsChanged = w.changed;
    }
    return r;
  });
}

function FileReview({ gh, pr, file, linked, anchor, session, refresh, notify }: {
  gh: GitHub; pr: PrDetails; file: PrFile; linked: boolean; anchor: string | undefined;
  session: ReviewSession; refresh: () => Promise<void>; notify: (m: string) => void;
}) {
  const [contents, setContents] = useState<Load<{ base: string; head: string }>>({ kind: 'loading' });
  const [mode, setMode] = useState<'whole' | 'changes'>('whole');
  const [view, setView] = useState<'rendered' | 'source'>('rendered');
  const [current, setCurrent] = useState(-1);

  useEffect(() => {
    let live = true;
    loadFileContents(gh, REPO, pr, file)
      .then((value) => { if (live) setContents({ kind: 'ready', value }); })
      .catch((e: unknown) => { if (live) setContents({ kind: 'error', message: errMsg(e) }); });
    return () => { live = false; };
    // Contents depend only on the commits and the file, not on threads (refresh keeps them).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gh, pr.headSha, pr.mergeBase, file.path, file.status, file.previousPath]);

  const diff = useMemo(() => (contents.kind === 'ready' ? diffFile(contents.value.base, contents.value.head) : null), [contents]);
  const rendering = useMemo(() => (diff ? renderItems(diff) : []), [diff]);
  const placed = useMemo(() => (diff ? placeThreads(pr.threads, file.path, diff) : null), [diff, pr.threads, file.path]);
  const changes = useMemo(() => (diff ? changeIndexes(diff.items) : []), [diff]);
  const toc = useMemo(() => (diff && placed ? outline(diff.items, placed) : []), [diff, placed]);
  // Where comments can go (SPEC §6): hunks from GitHub's patch headers, else computed locally.
  const units = useMemo(() => {
    if (!diff || contents.kind !== 'ready' || linked) return new Map<number, BlockUnits>();
    const { base, head } = contents.value;
    const hunks = file.patch ? hunksFromPatch(file.patch) : localHunks(base, head);
    return commentUnits(head, diff, hunks, changedHeadLines(base, head));
  }, [diff, contents, file.patch, linked]);
  const ctx = useMemo<ReviewCtx | null>(() => (contents.kind === 'ready'
    ? { pr, session, path: file.path, head: contents.value.head, refresh, notify } : null), [pr, session, file.path, contents, refresh, notify]);

  const goTo = useCallback((i: number) => {
    setCurrent(i);
    const el = document.getElementById(`item-${i}`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el?.focus({ preventScroll: true });
  }, []);
  const step = (dir: 1 | -1) => {
    if (!changes.length) return;
    const pos = changes.indexOf(current);
    const next = pos === -1 ? (dir === 1 ? 0 : changes.length - 1) : Math.min(changes.length - 1, Math.max(0, pos + dir));
    goTo(changes[next]!);
  };
  const changeNumber = changes.indexOf(current) + 1;

  // Links in this document: relative .md → Hashquill, other repo files → GitHub at the PR head,
  // #anchor → scroll to the heading (approved before M4).
  const resolver = useCallback<LinkResolver>((href) => {
    const l = resolveLink(href, file.path);
    switch (l.kind) {
      case 'external': return { href: l.href, external: true };
      case 'anchor': return { href: `#${anchorElementId(l.id)}`, external: false, onNavigate: () => scrollToAnchor(l.id) };
      case 'repo-file': return { href: githubBlobUrl(REPO.full, pr.headSha, l.path, l.anchor), external: true };
      case 'markdown': {
        if (l.path === file.path) return { href: `#${anchorElementId(l.anchor ?? '')}`, external: false, onNavigate: () => l.anchor && scrollToAnchor(l.anchor) };
        const r: Route = { name: 'pr', number: pr.number, file: l.path, ...(l.anchor ? { anchor: l.anchor } : {}) };
        return { href: routePath(r), external: false, onNavigate: () => navigate(r) };
      }
      case 'invalid': return { href: '#', external: false, onNavigate: () => {} };
    }
  }, [file.path, pr.headSha, pr.number]);

  // Arriving with #anchor (e.g. from another file's link): scroll once rendered.
  useEffect(() => { if (diff && anchor) requestAnimationFrame(() => scrollToAnchor(anchor)); }, [diff, anchor]);

  return (
    <>
      <div className="hq-filebar">
        <div className="hq-filebar__file">
          <span className="hq-mono">{file.path}</span>
          <span className="hq-mono hq-note">
            <span className="hq-linecount__add">+{diff?.added ?? file.additions}</span> <span className="hq-linecount__del">−{diff?.removed ?? file.deletions}</span>
            {diff && <> · {changes.length} {changes.length === 1 ? 'change' : 'changes'}</>}
          </span>
        </div>
        <div className="hq-filebar__tools">
          <div className="hq-stepper">
            <button type="button" className="hq-iconbtn" aria-label="Previous change" onClick={() => step(-1)} disabled={!changes.length}><ChevronUpIcon /></button>
            <span className="hq-note" aria-live="polite">{changes.length ? (changeNumber ? `Change ${changeNumber} of ${changes.length}` : `${changes.length} changes`) : 'No changes'}</span>
            <button type="button" className="hq-iconbtn" aria-label="Next change" onClick={() => step(1)} disabled={!changes.length}><ChevronDownIcon /></button>
          </div>
          <div className="hq-toggle" role="group" aria-label="How much of the file to show">
            <button type="button" aria-pressed={mode === 'whole'} onClick={() => setMode('whole')}>Whole file</button>
            <button type="button" aria-pressed={mode === 'changes'} onClick={() => setMode('changes')}>Changes only</button>
          </div>
          <div className="hq-toggle" role="group" aria-label="View">
            <button type="button" aria-pressed={view === 'rendered'} onClick={() => setView('rendered')}>Rendered</button>
            <button type="button" aria-pressed={view === 'source'} onClick={() => setView('source')}>Source</button>
          </div>
        </div>
      </div>

      <div className="hq-review">
        <aside className="hq-review__aside" aria-label="Files and outline">
          <div className="hq-review__group">
            <span className="hq-mono hq-note">Files changed</span>
            {linked && (
              <span className="hq-filelink" aria-current="page">
                <span className="hq-mono">{file.path}</span><span className="hq-mono hq-note">linked</span>
              </span>
            )}
            {pr.files.map((f) => (
              <a key={f.path} className="hq-filelink" aria-current={f.path === file.path ? 'page' : undefined}
                {...linkProps({ name: 'pr', number: pr.number, file: f.path })}
                onClick={(e) => { e.preventDefault(); navigate({ name: 'pr', number: pr.number, file: f.path }, { replace: true }); }}>
                <span className="hq-mono">{f.path}</span>
                <span className="hq-mono hq-note">{f.status === 'added' ? 'new' : f.status === 'removed' ? 'deleted' : `+${f.additions} −${f.deletions}`}</span>
              </a>
            ))}
          </div>
          {toc.length > 0 && view === 'rendered' && (
            <nav className="hq-review__group" aria-label={`Outline of ${file.path}`}>
              <span className="hq-mono hq-note">Outline</span>
              {toc.map((o) => (
                <a key={o.item} href={`#item-${o.item}`} className="hq-outline" style={{ paddingLeft: 8 + Math.max(0, o.depth - 1) * 12 }}
                  onClick={(e) => { e.preventDefault(); goTo(o.item); }}>
                  <span>{o.title}</span>
                  <span className="hq-outline__counts">
                    {o.changes > 0 && <span className="hq-chip hq-chip--small">{o.changes} {o.changes === 1 ? 'change' : 'changes'}</span>}
                    {o.comments > 0 && <span className="hq-note" aria-label={`${o.comments} comments`}><CommentIcon size={12} />{o.comments}</span>}
                  </span>
                </a>
              ))}
            </nav>
          )}
        </aside>

        <main className="hq-review__main">
          {contents.kind === 'loading' && <div aria-busy="true"><div className="hq-skeleton" style={{ width: '70%', height: 24 }} /><div className="hq-skeleton" style={{ width: '90%' }} /><div className="hq-skeleton" style={{ width: '80%' }} /></div>}
          {contents.kind === 'error' && <p className="hq-error" role="alert">Couldn’t load {file.path}: {contents.message}</p>}
          {linked && contents.kind === 'ready' && (
            <p className="hq-callout hq-callout--info">
              {contents.value.head ? <>Linked file, not changed in this pull request. Shown as it is on <code>{pr.headRef}</code>.</> : <>This linked file doesn’t exist on <code>{pr.headRef}</code>.</>}
            </p>
          )}
          {diff && placed && placed.file.length > 0 && (
            <section className="hq-filethreads" aria-label="Comments on the whole file">
              <h2 className="hq-note">Comments on the whole file</h2>
              <ReviewContext.Provider value={ctx}>
                {placed.file.map((t) => <ThreadView key={t.id} t={t} headSha={pr.headSha} prUrl={pr.url} canReply={!!ctx} />)}
              </ReviewContext.Provider>
            </section>
          )}
          {diff && view === 'source' && contents.kind === 'ready' && <SourceDiff base={contents.value.base} head={contents.value.head} />}
          <ReviewContext.Provider value={ctx}>
            <LinkContext.Provider value={resolver}>
              {diff && placed && view === 'rendered' && (
                <Blocks items={diff.items} rendering={rendering} threads={placed.byItem} units={units} mode={mode} current={current} headSha={pr.headSha} prUrl={pr.url} />
              )}
            </LinkContext.Provider>
          </ReviewContext.Provider>
        </main>
      </div>
    </>
  );
}

const KIND_LABEL: Record<DiffItem['kind'], string | undefined> = { same: undefined, added: 'Added', modified: 'Modified', removed: 'Removed' };

function Blocks({ items, rendering, threads, units, mode, current, headSha, prUrl }: {
  items: DiffItem[]; rendering: Rendering[]; threads: Map<number, PlacedThread[]>; units: Map<number, BlockUnits>;
  mode: 'whole' | 'changes'; current: number; headSha: string; prUrl: string;
}) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const out: React.ReactNode[] = [];
  for (let i = 0; i < items.length; i++) {
    // "Changes only": collapse runs of unchanged blocks without comments.
    if (mode === 'changes' && items[i]!.kind === 'same' && !threads.has(i) && !expanded.has(i)) {
      let j = i;
      while (j < items.length && items[j]!.kind === 'same' && !threads.has(j)) j++;
      const start = i, n = j - i;
      out.push(
        <div key={`gap-${start}`} className="hq-gap">
          <button type="button" onClick={() => setExpanded((s) => new Set([...s, ...Array.from({ length: n }, (_, k) => start + k)]))}>
            {n} unchanged {n === 1 ? 'block' : 'blocks'} · Show
          </button>
        </div>,
      );
      i = j - 1;
      continue;
    }
    out.push(<BlockRow key={i} i={i} item={items[i]!} r={rendering[i]!} threads={threads.get(i) ?? []} units={units.get(i)} current={current === i} headSha={headSha} prUrl={prUrl} />);
  }
  return <div className="hq-doc">{out}</div>;
}

function Content({ block }: { block: RenderedBlock | undefined }) {
  if (!block) return null;
  return block.kind === 'mermaid' ? <Mermaid source={block.source} /> : <Rendered hast={block.hast} />;
}

function BlockRow({ i, item, r, threads, units, current, headSha, prUrl }: {
  i: number; item: DiffItem; r: Rendering; threads: PlacedThread[]; units: BlockUnits | undefined; current: boolean; headSha: string; prUrl: string;
}) {
  const label = KIND_LABEL[item.kind];
  const contentRef = useRef<HTMLDivElement>(null);
  return (
    <div id={`item-${i}`} className={`hq-blk hq-blk--${item.kind}${current ? ' hq-blk--current' : ''}`} tabIndex={-1}>
      <span className="hq-bar" title={label} aria-hidden="true" />
      <div className="hq-blk__content" ref={contentRef}>
        {label && <span className="hq-visually-hidden">{label}: </span>}
        {item.kind === 'removed' ? <div className="hq-removed"><Content block={r.base} /></div> : <Content block={r.head} />}
        {item.kind === 'modified' && item.formattingOnly && <p className="hq-note">Only the Markdown formatting changed. Switch to Source to see it.</p>}
        {item.kind === 'modified' && r.head?.kind === 'mermaid' && <p className="hq-note">Diagram changed. Switch to Source to see how.</p>}
      </div>
      <div className="hq-blk__side">
        {units
          ? <UnitButtons units={units} contentRef={contentRef} threads={threads}
              renderThreads={(ts) => <ThreadBadge threads={ts} headSha={headSha} prUrl={prUrl} />} />
          : threads.length > 0 && <ThreadBadge threads={threads} headSha={headSha} prUrl={prUrl} />}
      </div>
    </div>
  );
}

function ThreadBadge({ threads, headSha, prUrl }: { threads: PlacedThread[]; headSha: string; prUrl: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const count = threads.reduce((n, t) => n + t.comments.length, 0);
  const allOutdated = threads.every((t) => t.outdated);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [open]);
  const label = `${count} ${allOutdated ? 'outdated ' : ''}${count === 1 ? 'comment' : 'comments'}`;
  return (
    <div className="hq-threadbadge" ref={ref}>
      <button type="button" className={`hq-badge${allOutdated ? ' hq-badge--outdated' : ''}`} aria-label={label} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <CommentIcon size={13} />{count}
      </button>
      {open && (
        <div className="hq-popover" role="dialog" aria-label={label}>
          <button type="button" className="hq-iconbtn hq-popover__close" aria-label="Close" onClick={() => setOpen(false)}><CloseIcon size={14} /></button>
          {threads.map((t) => <ThreadView key={t.id} t={t} headSha={headSha} prUrl={prUrl} canReply />)}
        </div>
      )}
    </div>
  );
}

const when = (iso: string) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); };

function ThreadView({ t, headSha, prUrl, canReply = false }: { t: PlacedThread; headSha: string; prUrl: string; canReply?: boolean }) {
  const [show, setShow] = useState(!t.outdated);
  return (
    <div className={`hq-thread${t.outdated ? ' hq-thread--outdated' : ''}`}>
      {(t.outdated || t.isResolved) && (
        <div className="hq-thread__flags">
          {t.outdated && (
            <span className="hq-status hq-status--open" title={`Current head of the pull request: ${headSha.slice(0, 7)}`}>
              Outdated · the text changed after this comment
            </span>
          )}
          {t.isResolved && <span className="hq-status hq-status--approved">Resolved</span>}
          {t.outdated && <button type="button" className="hq-linkbtn" onClick={() => setShow((s) => !s)}>{show ? 'Hide' : 'Show'}</button>}
        </div>
      )}
      {show && t.comments.map((c) => (
        <article key={c.id} className="hq-comment">
          <header><strong>{c.author}</strong> <span className="hq-note">{when(c.createdAt)}</span>
            {c.pending && <span className="hq-status hq-status--review-requested" title="Part of your review. Not visible to others until you submit.">Pending</span>}
            {c.url && <a className="hq-note" href={c.url} target="_blank" rel="noopener noreferrer" aria-label="Open this comment on GitHub"><ExternalIcon size={12} /></a>}
          </header>
          <div className="hq-comment__body"><Rendered hast={renderMarkdown(c.body, { githubRepo: REPO.full })} /></div>
        </article>
      ))}
      {show && t.commentsTruncated && <TruncatedNotice url={t.comments[0]?.url ?? prUrl} />}
      {show && canReply && <ReplyBox threadId={t.id} fileThread={t.subjectType === 'FILE'} />}
    </div>
  );
}

function SourceDiff({ base, head }: { base: string; head: string }) {
  const rows: { kind: 'same' | 'add' | 'del'; a?: number; b?: number; text: string }[] = [];
  let a = 1, b = 1;
  for (const part of diffLines(base, head)) {
    const lines = part.value.replace(/\n$/, '').split('\n');
    for (const text of lines) {
      if (part.added) rows.push({ kind: 'add', b: b++, text });
      else if (part.removed) rows.push({ kind: 'del', a: a++, text });
      else rows.push({ kind: 'same', a: a++, b: b++, text });
    }
  }
  return (
    <table className="hq-source" aria-label="Source diff">
      <tbody>
        {rows.map((r, i) => (
          <Fragment key={i}>
            <tr className={`hq-source__${r.kind}`}>
              <td className="hq-source__num" aria-label={r.a ? `old line ${r.a}` : undefined}>{r.a ?? ''}</td>
              <td className="hq-source__num" aria-label={r.b ? `new line ${r.b}` : undefined}>{r.b ?? ''}</td>
              <td className="hq-source__sign" aria-label={r.kind === 'add' ? 'added' : r.kind === 'del' ? 'removed' : undefined}>{r.kind === 'add' ? '+' : r.kind === 'del' ? '−' : ''}</td>
              <td className="hq-source__text">{r.text}</td>
            </tr>
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

/** GitHub limits how many threads/comments one request returns: say so, never drop silently. */
function TruncatedNotice({ url }: { url: string }) {
  return (
    <p className="hq-callout hq-callout--info" role="note">
      Some comments aren’t shown here · <a href={url} target="_blank" rel="noopener noreferrer">Open on GitHub<ExternalIcon size={12} /></a>
    </p>
  );
}

function scrollToAnchor(id: string) {
  const el = document.getElementById(anchorElementId(id));
  if (!el) return;
  el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  el.setAttribute('tabindex', '-1');
  el.focus({ preventScroll: true });
}
